import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Container,
  Tab,
  Tabs,
  Typography,
  styled,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ChevronDownIcon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import eulaSvg from '../assets/images/eula-button.svg';
import faqBg from '../assets/images/faq-bg.png';

const AccordionWrapper = styled(Accordion)({
  borderRadius: '0px !important',
  border: 0,
  boxShadow: 'none !important',
  background: '#fff !important',
  paddingTop: '8px',
  paddingBottom: '8px',
  '&::before': {
    background: 'transparent !important',
  },
});

interface GlossaryItem {
  title: string;
  text: string;
}

interface GroupedGlossary {
  [letter: string]: GlossaryItem[];
}

function HelpCenter() {
  const [selectedTab, setSelectedTab] = useState(0);

  const {
    data: glossaryData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['glossary'],
    queryFn: async () => {
      const glossaryUrl = import.meta.env.VITE_REACT_APP_GLOSSARY_URL;
      const response = await fetch(glossaryUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch glossary: ${response.statusText}`);
      }

      return (await response.json()) as GlossaryItem[];
    },
  });

  const groupedGlossary: GroupedGlossary = {};
  const letters: string[] = [];

  if (glossaryData) {
    glossaryData.forEach((item) => {
      const firstLetter = item.title[0].toUpperCase();
      if (!groupedGlossary[firstLetter]) {
        groupedGlossary[firstLetter] = [];
      }
      groupedGlossary[firstLetter].push(item);
    });
    letters.push(...Object.keys(groupedGlossary).sort());
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'primary.main',
          backgroundImage: `url(${faqBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          height: '300px',
        }}
      >
        <Typography variant='h4' sx={{ color: '#fff', fontWeight: 600, textAlign: 'center' }}>
          Glossary
        </Typography>
      </Box>
      <Container sx={{ display: 'flex', flexDirection: 'row', gap: 10, mt: 10, mb: 10 }}>
        <Box>
          <Link to='/eula' style={{ textDecoration: 'none' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                background: '#fff',
                boxShadow: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
                padding: '16px 48px',
                borderRadius: '8px',
                transition: 'transform 0.2s',

                '&:hover': {
                  transform: 'scale(1.01)',
                },
              }}
            >
              <img src={eulaSvg} alt='End-User License Agreement' />
              <Typography sx={{ textAlign: 'center', fontWeight: 500, color: 'text.primary' }}>
                End-User License Agreement
              </Typography>
            </Box>
          </Link>
        </Box>
        <Box sx={{ flex: 1 }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                background: '#fff',
                boxShadow: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
                padding: '16px 48px',
                borderRadius: '8px',
              }}
            >
              <Typography variant='h6' color='error'>
                Error loading glossary
              </Typography>
              <Typography variant='body2' sx={{ mt: 1 }}>
                {error instanceof Error ? error.message : 'Failed to load glossary'}
              </Typography>
            </Box>
          )}

          {!isLoading && !error && letters.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                background: '#fff',
                boxShadow: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
                padding: '16px 48px',
                borderRadius: '8px',
              }}
            >
              <Typography variant='h6' sx={{ fontWeight: 'bold', marginBottom: 2 }}>
                Glossary
              </Typography>

              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  variant='scrollable'
                  scrollButtons='auto'
                  sx={{
                    '& .MuiTab-root': {
                      minWidth: '48px',
                      fontSize: '1rem',
                      fontWeight: 500,
                    },
                  }}
                >
                  {letters.map((letter) => (
                    <Tab key={letter} label={letter} />
                  ))}
                </Tabs>
              </Box>

              <Box>
                {letters[selectedTab] && (
                  <>
                    {groupedGlossary[letters[selectedTab]].map((item, index) => (
                      <AccordionWrapper
                        key={`${letters[selectedTab]}-${index}`}
                        elevation={0}
                        disableGutters
                        defaultExpanded={`${index}` === '0'}
                      >
                        <AccordionSummary expandIcon={<ChevronDownIcon />}>
                          <Typography
                            sx={{
                              fontWeight: 500,
                            }}
                          >
                            {item.title}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography
                            sx={{
                              fontSize: 14,
                            }}
                          >
                            {item.text}
                          </Typography>
                        </AccordionDetails>
                      </AccordionWrapper>
                    ))}
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </>
  );
}

export default HelpCenter;
